import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, App, Row, Col, Typography, Dropdown, Tag, Tooltip, Alert, Badge, Segmented, Tabs } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { PlusOutlined, DeleteOutlined, FilterOutlined, ClearOutlined, SearchOutlined, MoreOutlined, EditOutlined, SyncOutlined, ArrowUpOutlined, ArrowDownOutlined, TeamOutlined, ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import { CustomerOptionDto } from '../dto/reference';
import { formatDate } from '../utils/dateUtils';
import { ORDER_STATUS_COLORS } from '../utils/orderStatus';
import { getDisplayOrderNumber } from '../types/order';
import {
  filterAndSortOrders,
  formatOrderTableDeliveryLocation,
  getOrderTableDeliveryLocationLines,
  getOrderTableFactory,
  getOrderTableProductEntries,
  type OrderTableFilters,
  type OrderTableSortField,
} from './orderTableUtils';
import { fetchCustomerOptions } from '../modules/reference-data/api/referenceDataApi';
import { matchesCustomerPickerSearch } from '../modules/reference-data/customerSearch';
import {
  OrderStatusFormValues,
  useOrderStatusChangeWithConfirmation,
} from '../modules/orders/useOrderStatusChangeWithConfirmation';
import {
  IN_PROGRESS_ORDER_VIEW_STATUSES,
  NEW_ORDER_VIEW_STATUSES,
  countOrdersByStatuses,
  orderStatusFilterMatchesView,
} from '../modules/orders/orderViewFilters';
import {
  CUSTOMER_ORDER_ACTIVE_STATUS_VALUES,
  getOrderAllowedTransitions,
  isActiveCustomerOrderStatus,
} from '../modules/orders/orderStatusTransitions';
import {
  SUPPLIER_ORDER_STATUSES,
  type SupplierOrderStatus,
} from '../modules/orders/supplierOrderStatuses';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import {
  canChangeCustomerOrderStatus,
  canCreateCustomerOrders,
  canDeleteCustomerOrders,
  canEditCustomerOrderDetails,
  canManageCustomerOrderOperations,
  canManageCustomerOrderSupplierLinks,
  isCustomerOrderTesterMode,
} from '../modules/orders/customerOrderAccessPolicy';
import CustomerContactCell from '../components/customers/CustomerContactCell';
import { renderColumnTitle } from '../modules/orders/components/ordersTableColumnTitle';
import SupplierOrdersPanel, {
  SupplierOrderFilters,
  SupplierOrderSortField,
} from '../modules/orders/sections/SupplierOrdersPanel';
import {
  canUseSupplierOrderFinancialSortFields,
  shouldResetSupplierOrderFinancialSort,
} from '../modules/orders/supplierOrderSortVisibility';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { getOrderErrorMessage, isBlockingOrderError } from '../modules/orders/orderErrorMessages';
import '../components/layout/AppPage.css';
import './Orders.css';

const { Text } = Typography;
const EMPTY_VALUE = '-';
const ORDER_FLOW_PARAM = 'orderFlow';
type OrderFlow = 'customer' | 'supplier';
const SUPPLIER_ORDER_STATUS_FILTER_OPTIONS = [...SUPPLIER_ORDER_STATUSES];
const SUPPLIER_ORDER_IN_PROGRESS_STATUSES: SupplierOrderStatus[] = [
  'SENT',
  'CONFIRMED',
  'IN_PRODUCTION',
  'READY',
  'SHIPPED',
  'PARTIALLY_RECEIVED',
];

const ORDER_STATUS_FILTER_OPTIONS = [...CUSTOMER_ORDER_ACTIVE_STATUS_VALUES];


type OrdersPageFilters = Omit<OrderTableFilters, 'dateRange'> & {
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
};

type SupplierOrdersPageFilters = Omit<SupplierOrderFilters, 'dateRange'> & {
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
};

const DEFAULT_ORDER_FILTERS: OrdersPageFilters = {
  status: [],
  customerId: undefined,
  accountManagerId: undefined,
  dateRange: null,
  search: '',
  orderNumber: '',
  factory: '',
  products: '',
  deliveryLocation: '',
  clientOrderNumber: '',
  proformaNumber: '',
  deadline: undefined,
  sortBy: 'orderNumber',
  sortOrder: 'desc',
};

const DEFAULT_SUPPLIER_ORDER_FILTERS: SupplierOrdersPageFilters = {
  status: [],
  search: '',
  orderNumber: '',
  supplier: '',
  factory: '',
  products: '',
  proformaNumber: '',
  dateRange: null,
  sortBy: 'orderNumber',
  sortOrder: 'desc',
};

const CUSTOMER_TABLE_SORT_FIELDS: OrderTableSortField[] = [
  'orderNumber',
  'status',
  'producer',
  'customer',
  'contact',
  'products',
  'date',
  'deliveryLocation',
  'clientOrderNumber',
  'proformaNumber',
];

const isCustomerTableSortField = (value: unknown): value is OrderTableSortField =>
  typeof value === 'string' && CUSTOMER_TABLE_SORT_FIELDS.includes(value as OrderTableSortField);

const parseOrderStatusFilter = (value: string | null) => {
  if (!value) return [];

  return value
    .split(',')
    .map((status) => status.trim())
    .filter(isActiveCustomerOrderStatus);
};

const parseDeadlineFilter = (value: string | null): OrdersPageFilters['deadline'] =>
  value === 'upcoming' || value === 'overdue' ? value : undefined;

const getInitialFiltersFromSearchParams = (searchParams: URLSearchParams): OrdersPageFilters => ({
  ...DEFAULT_ORDER_FILTERS,
  status: parseOrderStatusFilter(searchParams.get('status')),
  search: searchParams.get('search') || '',
  orderNumber: searchParams.get('orderNumber') || '',
  deadline: parseDeadlineFilter(searchParams.get('deadline')),
});

const renderTablePreviewValue = (
  value?: string | null,
  className = 'orders-table__cell-text'
) =>
  value ? (
    <Tooltip title={value}>
      <span className={className}>{value}</span>
    </Tooltip>
  ) : (
    <Text type="secondary">{EMPTY_VALUE}</Text>
  );

const renderOptionalDate = (value?: string | null) =>
  value ? (
    <span className="orders-table__date-preview">{formatDate(value)}</span>
  ) : (
    <Text type="secondary">{EMPTY_VALUE}</Text>
  );

const renderProductPreviewCell = (products?: any[] | null) => {
  const productEntries = getOrderTableProductEntries(products, EMPTY_VALUE);

  if (productEntries.length === 0) {
    return <Text type="secondary">{EMPTY_VALUE}</Text>;
  }

  return (
    <div className="orders-table__products-cell">
      {productEntries.map((entry) => (
        <div key={entry.key} className="orders-table__product-row">
          <div className="orders-table__product-main">
            <Tooltip title={entry.name}>
              <span className="orders-table__product-name">{entry.name}</span>
            </Tooltip>
            {entry.dimensionsLabel ? (
              <span className="orders-table__product-dimensions">
                {entry.dimensionsLabel}
              </span>
            ) : null}
          </div>
          <span className="orders-table__product-quantity">
            {entry.quantityLabel ?? ''}
          </span>
        </div>
      ))}
    </div>
  );
};

const renderDeliveryLocationCell = (value?: string | null) => {
  const lines = getOrderTableDeliveryLocationLines(value, EMPTY_VALUE);
  const tooltipTitle = formatOrderTableDeliveryLocation(value, EMPTY_VALUE);

  if (lines.length === 0) {
    return <Text type="secondary">{EMPTY_VALUE}</Text>;
  }

  return (
    <Tooltip title={tooltipTitle}>
      <span className="orders-table__delivery-preview">
        {lines.map((line) => (
          <span key={line} className="orders-table__delivery-line">
            {line}
          </span>
        ))}
      </span>
    </Tooltip>
  );
};

const getOrderStatusColor = (status?: string | null) => {
  if (!status) {
    return 'default';
  }

  return ORDER_STATUS_COLORS[status] || 'default';
};

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['orders', 'common', 'customers']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [orders, setOrders] = useState<any[]>([]);
  const [customers, setCustomers] = useState<CustomerOptionDto[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createChoiceVisible, setCreateChoiceVisible] = useState(false);
  const [supplierFilters, setSupplierFilters] = useState<SupplierOrdersPageFilters>({ ...DEFAULT_SUPPLIER_ORDER_FILTERS });
  const [supplierFilteredCount, setSupplierFilteredCount] = useState(0);
  const [bulkActionLoading, setBulkActionLoading] = useState<'delete' | 'convert' | null>(null);
  const [orderPendingDelete, setOrderPendingDelete] = useState<any | null>(null);
  const [orderPendingStatusChange, setOrderPendingStatusChange] = useState<any | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [statusForm] = Form.useForm();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const currentUser = authService.getCurrentUser();
  const isTester = isCustomerOrderTesterMode(currentUser);
  const canCreateOrder = canCreateCustomerOrders(currentUser);
  const canDeleteOrder = canDeleteCustomerOrders(currentUser);
  const canManageOrderOperations = canManageCustomerOrderOperations(currentUser);
  const canEditOrderDetails = canEditCustomerOrderDetails(currentUser);
  const canViewSupplierOrders = canManageCustomerOrderSupplierLinks(currentUser);
  const canCreateSupplierOrder = canManageCustomerOrderSupplierLinks(currentUser);
  const canViewSupplierOrderPurchasePrices = authService.canViewSupplierOrderPurchasePrices();
  const canChangeOrderStatus = canChangeCustomerOrderStatus(currentUser);
  const requestedOrderFlow: OrderFlow = searchParams.get(ORDER_FLOW_PARAM) === 'supplier' ? 'supplier' : 'customer';
  const orderFlow: OrderFlow = requestedOrderFlow === 'supplier' && canViewSupplierOrders ? 'supplier' : 'customer';

  const [showQuotes, setShowQuotes] = useState(false);

  useEffect(() => {
    if (canUseSupplierOrderFinancialSortFields(canViewSupplierOrderPurchasePrices)) {
      return;
    }

    setSupplierFilters((current) => (
      shouldResetSupplierOrderFinancialSort(canViewSupplierOrderPurchasePrices, current.sortBy)
        ? { ...current, sortBy: DEFAULT_SUPPLIER_ORDER_FILTERS.sortBy }
        : current
    ));
  }, [canViewSupplierOrderPurchasePrices]);

  const {
    submitting: statusUpdating,
    submitStatusChange,
  } = useOrderStatusChangeWithConfirmation({
    orderId: orderPendingStatusChange?.id,
    onSuccess: async (values, result) => {
      feedback.success(t('orders:orderDetail.messages.statusUpdateSuccess', {
        orderNumber: orderPendingStatusChange ? getDisplayOrderNumber(orderPendingStatusChange) : '',
        status: t(`orders:statusOptions.${values.status}`),
      }));
      if (result.operationalWarning) {
        feedback.warning(result.operationalWarning);
      }
      setOrderPendingStatusChange(null);
      statusForm.resetFields();
      await fetchOrders();
    },
    onError: () => {
      feedback.error(t('orders:orderDetail.messages.statusUpdateError', {
        orderNumber: orderPendingStatusChange ? getDisplayOrderNumber(orderPendingStatusChange) : '',
      }));
    },
  });

  // Filter states
  const [filters, setFilters] = useState<OrdersPageFilters>(() => getInitialFiltersFromSearchParams(searchParams));

  useEffect(() => {
    if (requestedOrderFlow !== 'supplier' || canViewSupplierOrders) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(ORDER_FLOW_PARAM);
    setSearchParams(nextSearchParams, { replace: true });
  }, [canViewSupplierOrders, requestedOrderFlow, searchParams, setSearchParams]);

  const syncUrlBackedFilters = (nextFilters: OrdersPageFilters) => {
    const nextSearchParams = new URLSearchParams();

    if (nextFilters.status.length > 0) {
      nextSearchParams.set('status', nextFilters.status.join(','));
    }

    if (nextFilters.search.trim()) {
      nextSearchParams.set('search', nextFilters.search.trim());
    }

    if (nextFilters.orderNumber.trim()) {
      nextSearchParams.set('orderNumber', nextFilters.orderNumber.trim());
    }

    if (nextFilters.deadline) {
      nextSearchParams.set('deadline', nextFilters.deadline);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    setFilters((current) => {
      const nextUrlFilters = getInitialFiltersFromSearchParams(searchParams);

      if (
        current.status.join(',') === nextUrlFilters.status.join(',') &&
        current.search === nextUrlFilters.search &&
        current.orderNumber === nextUrlFilters.orderNumber &&
        current.deadline === nextUrlFilters.deadline
      ) {
        return current;
      }

      return {
        ...current,
        status: nextUrlFilters.status,
        search: nextUrlFilters.search,
        orderNumber: nextUrlFilters.orderNumber,
        deadline: nextUrlFilters.deadline,
      };
    });
  }, [searchParams]);

  useEffect(() => {
    if (orderFlow !== 'customer') {
      return;
    }

    if (!isTester) fetchCustomers();
    fetchSuppliers();
    if (!isTester) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFlow]);

  useEffect(() => {
    if (orderFlow !== 'customer') {
      return;
    }

    if (!isTester) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFlow, showQuotes]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (showQuotes) count++;
    if (filters.status.length > 0) count++;
    if (filters.customerId) count++;
    if (filters.accountManagerId) count++;
    if (filters.dateRange) count++;
    if (filters.search) count++;
    if (filters.orderNumber) count++;
    if (filters.factory) count++;
    if (filters.products) count++;
    if (filters.deliveryLocation) count++;
    if (filters.clientOrderNumber) count++;
    if (filters.proformaNumber) count++;
    if (filters.deadline) count++;
    return count;
  }, [filters, showQuotes]);

  const supplierActiveFilterCount = useMemo(() => {
    let count = 0;
    if (supplierFilters.status.length > 0) count++;
    if (supplierFilters.search) count++;
    if (supplierFilters.orderNumber) count++;
    if (supplierFilters.supplier) count++;
    if (supplierFilters.factory) count++;
    if (supplierFilters.products) count++;
    if (supplierFilters.proformaNumber) count++;
    if (supplierFilters.dateRange) count++;
    return count;
  }, [supplierFilters]);

  const currentActiveFilterCount = orderFlow === 'supplier' ? supplierActiveFilterCount : activeFilterCount;

  const supplierNamesById = useMemo(
    () =>
      new Map(
        suppliers.map((supplier: any) => [supplier.id, supplier.name] as const)
      ),
    [suppliers]
  );

  const customerNamesById = useMemo(
    () => new Map(customers.map((customer) => [customer.id, customer.name] as const)),
    [customers]
  );

  const customerSelectOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: customer.id,
        label: customer.name,
        name: customer.name,
        taxId: customer.taxId || null,
        isBranch: Boolean(customer.isBranch),
        parentName: customer.parentCustomerId ? customerNamesById.get(customer.parentCustomerId) || null : null,
        customerCategoryName: customer.customerCategoryName || null,
        customerCategoryColor: customer.customerCategoryColor || null,
      })),
    [customerNamesById, customers]
  );

  const filterCustomerOption = (
    input: string,
    option?: { name?: string; taxId?: string | null; parentName?: string | null }
  ) =>
    matchesCustomerPickerSearch(
      {
        name: option?.name || '',
        taxId: option?.taxId || null,
        parentName: option?.parentName || null,
      },
      input
    );

  const renderCustomerOption = (option: any) => {
    const { label, taxId, isBranch, parentName, customerCategoryName, customerCategoryColor } = option.data as {
      label: string;
      taxId?: string | null;
      isBranch?: boolean;
      parentName?: string | null;
      customerCategoryName?: string | null;
      customerCategoryColor?: string | null;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Space size={8}>
          <span>{label}</span>
          {isBranch ? <Tag bordered={false}>{t('customers:preview.branch')}</Tag> : null}
          {customerCategoryName ? (
            <Tag color={customerCategoryColor || 'default'}>{customerCategoryName}</Tag>
          ) : null}
        </Space>
        {parentName ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('customers:preview.parentCustomer')}: {parentName}
          </Text>
        ) : null}
        {taxId ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            NIP: {taxId}
          </Text>
        ) : null}
      </div>
    );
  };

  const clearFilters = () => {
    setFilters({ ...DEFAULT_ORDER_FILTERS });
    setShowQuotes(false);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const clearSupplierFilters = () => {
    setSupplierFilters({ ...DEFAULT_SUPPLIER_ORDER_FILTERS });
  };

  const clearCurrentFilters = () => {
    if (orderFlow === 'supplier') {
      clearSupplierFilters();
      return;
    }

    clearFilters();
  };

  const displayedOrders = useMemo(
    () =>
      filterAndSortOrders(
        orders,
        {
          ...filters,
          dateRange: filters.dateRange
            ? [
                filters.dateRange[0]?.startOf('day').toDate() || null,
                filters.dateRange[1]?.endOf('day').toDate() || null,
              ]
            : null,
        },
        supplierNamesById,
        EMPTY_VALUE
      ),
    [filters, orders, supplierNamesById]
  );

  const supplierTableFilters = useMemo<SupplierOrderFilters>(
    () => ({
      ...supplierFilters,
      dateRange: supplierFilters.dateRange
        ? [
            supplierFilters.dateRange[0]?.startOf('day').toDate() || null,
            supplierFilters.dateRange[1]?.endOf('day').toDate() || null,
          ]
        : null,
    }),
    [supplierFilters]
  );

  useEffect(() => {
    setSelectedOrderIds((current) =>
      current.filter((id) => displayedOrders.some((order) => order.id === id))
    );
  }, [displayedOrders]);

  const selectedOrders = useMemo(
    () => displayedOrders.filter((order) => Boolean(order.id && selectedOrderIds.includes(order.id))),
    [displayedOrders, selectedOrderIds]
  );

  const newOrdersCount = useMemo(
    () => countOrdersByStatuses(orders, NEW_ORDER_VIEW_STATUSES),
    [orders]
  );

  const inProgressOrdersCount = useMemo(
    () => countOrdersByStatuses(orders, IN_PROGRESS_ORDER_VIEW_STATUSES),
    [orders]
  );

  const showAllOrdersView = () => {
    setShowQuotes(false);
    updateFilter('status', []);
  };

  const showNewOrdersView = () => {
    setShowQuotes(false);
    updateFilter('status', NEW_ORDER_VIEW_STATUSES);
  };

  const showInProgressOrdersView = () => {
    setShowQuotes(false);
    updateFilter('status', IN_PROGRESS_ORDER_VIEW_STATUSES);
  };

  const showQuotesView = () => {
    setShowQuotes(true);
    updateFilter('status', []);
  };

  const showCustomerOrderFlow = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(ORDER_FLOW_PARAM);
    setSearchParams(nextSearchParams);
  };

  const showSupplierOrderFlow = () => {
    if (!canViewSupplierOrders) {
      return;
    }

    const nextSearchParams = new URLSearchParams();
    nextSearchParams.set(ORDER_FLOW_PARAM, 'supplier');
    setSearchParams(nextSearchParams);
    setShowQuotes(false);
    setFilters({ ...DEFAULT_ORDER_FILTERS });
    clearSelection();
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();

      params.append('isQuote', String(showQuotes));

      const response = await api.get(`/orders${params.toString() ? '?' + params.toString() : ''}`);
      setOrders(response.data);
    } catch (error) {
      feedback.error(t('orders:messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchCustomerOptions('orders.customerOptions');
      setCustomers(response);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch only users marked as available notification recipients
      const response = await api.get('/notifications/available-recipients');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch available recipients');
    }
  };

  const openCreateOrderChoiceModal = () => {
    if (!canViewSupplierOrders || !canCreateSupplierOrder) {
      navigate('/app/orders/new');
      return;
    }

    setCreateChoiceVisible(true);
  };

  const handleCreateCustomerOrderChoice = () => {
    setCreateChoiceVisible(false);
    navigate('/app/orders/new');
  };

  const handleCreateSupplierOrderChoice = () => {
    if (!canCreateSupplierOrder) {
      return;
    }

    setCreateChoiceVisible(false);
    navigate('/app/supplier-orders/new');
  };

  const handleDeleteOrder = async (orderId: string, orderNumber?: string, customerName?: string) => {
    try {
      await api.delete(`/orders/${orderId}`);
      const details = [orderNumber, customerName].filter(Boolean).join(' — ');
      feedback.success(t('orders:orderDeletedDetails', { details }));
      fetchOrders();
    } catch (error: any) {
      // Shared mapper: the rejection names the same transports / WZ documents
      // the order card lists under "Niedostępne akcje".
      feedback.error(getOrderErrorMessage(error, t, 'orders:messages.deleteError', { orderNumber: orderNumber || '' }), {
        sticky: isBlockingOrderError(error),
      });
    }
  };

  const clearSelection = () => {
    setSelectedOrderIds([]);
  };

  const openStatusModal = (order: any) => {
    setOrderPendingStatusChange(order);
    statusForm.resetFields();
    statusForm.setFieldsValue({
      status: undefined,
      notes: '',
    });
  };

  const handleStatusChange = async (values: OrderStatusFormValues) => {
    await submitStatusChange(values);
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.length === 0) {
      return;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:bulk.deleteConfirmTitle', { count: selectedOrders.length }),
        content: t('orders:bulk.deleteConfirmDescription'),
        okText: t('orders:bulk.delete'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    setBulkActionLoading('delete');

    try {
      for (const order of selectedOrders) {
        await api.delete(`/orders/${order.id}`);
      }

      feedback.success(t('orders:bulk.deleteSuccessDescription', { count: selectedOrders.length }));
      clearSelection();
      fetchOrders();
    } catch (error: any) {
      if (isBlockingOrderError(error)) {
        feedback.error(getOrderErrorMessage(error, t, 'orders:bulk.deleteError'), { sticky: true });
        // Refresh so any orders already deleted before the guard hit drop out of view.
        fetchOrders();
      } else {
        feedback.error(t('orders:bulk.deleteError'));
      }
    } finally {
      setBulkActionLoading(null);
    }
  };

  const handleBulkConvert = async () => {
    if (selectedOrders.length === 0) {
      return;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:bulk.convertConfirmTitle', { count: selectedOrders.length }),
        content: t('orders:bulk.convertConfirmDescription'),
        okText: t('orders:bulk.convert'),
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    setBulkActionLoading('convert');

    try {
      for (const order of selectedOrders) {
        await api.post(`/orders/${order.id}/convert-quote`);
      }

      feedback.success(t('orders:bulk.convertSuccess', { count: selectedOrders.length }));
      clearSelection();
      fetchOrders();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:bulk.convertError'));
    } finally {
      setBulkActionLoading(null);
    }
  };

  const getRowMenuItems = (record: any): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [];

    if (canEditOrderDetails) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: t('common:edit')
      });
    }

    if (canChangeOrderStatus) {
      items.push({
        key: 'status',
        icon: <SyncOutlined />,
        label: t('orders:orderDetail.updateStatus')
      });
    }

    if (showQuotes && canManageOrderOperations) {
      items.push({
        key: 'convert',
        label: t('orders:convertQuote')
      });
    }

    if (canDeleteOrder) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('orders:deleteOrder'),
        danger: true
      });
    }

    return items;
  };

  const columns = useMemo(() => {
    const getColumnSortProps = (field: OrderTableSortField): { sorter: true; sortOrder: SortOrder } => ({
      sorter: true,
      sortOrder: filters.sortBy === field ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    });

    const cols: any[] = [
      {
        title: renderColumnTitle(t('orders:table.customer')),
        key: 'customer',
        ...getColumnSortProps('customer'),
        width: '23%',
        render: (_: unknown, record: any) => {
          const displayOrderNumber = getDisplayOrderNumber(record);

          return (
            <span className="customer-orders-table__customer">
              <span className="customer-orders-table__customer-name">
                {record.customer?.name || EMPTY_VALUE}
              </span>
              <span className="customer-orders-table__order-meta">
                <Tooltip title={displayOrderNumber}>
                  <Button
                    className="customer-orders-table__order-link"
                    type="link"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/app/orders/${record.id}`);
                    }}
                  >
                    {displayOrderNumber}
                  </Button>
                </Tooltip>
                <Tag
                  color={getOrderStatusColor(record.status)}
                  className="customer-orders-table__status"
                >
                  {t(`orders:statusOptions.${record.status}`, {
                    defaultValue: t('common:unknownStatus'),
                  })}
                </Tag>
              </span>
              {(record.customer?.contactPeople?.length || record.customer?.contactPerson || record.customer?.phone) ? (
                <span className="customer-orders-table__contact">
                  <CustomerContactCell
                    contacts={record.customer?.contactPeople}
                    contactPerson={record.customer?.contactPerson}
                    phone={record.customer?.phone}
                    emptyValue={EMPTY_VALUE}
                  />
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        title: renderColumnTitle(t('orders:table.productsSummary')),
        key: 'products',
        ...getColumnSortProps('products'),
        width: '28%',
        className: 'orders-table__products-column',
        render: (_: unknown, record: any) => renderProductPreviewCell(record.products),
      },
      {
        title: renderColumnTitle(t('orders:table.producer')),
        key: 'producer',
        ...getColumnSortProps('producer'),
        width: '13%',
        render: (_: unknown, record: any) =>
          renderTablePreviewValue(
            getOrderTableFactory(record, supplierNamesById, EMPTY_VALUE),
            'orders-table__factory-preview'
          ),
      },
      {
        title: renderColumnTitle(t('orders:table.delivery')),
        key: 'date',
        ...getColumnSortProps('date'),
        width: '18%',
        render: (_: unknown, record: any) => (
          <span className="customer-orders-table__delivery">
            <span className="orders-table__delivery-date">
              {renderOptionalDate(record.expectedDelivery || record.orderDate)}
            </span>
            <span className="customer-orders-table__delivery-location">
              {renderDeliveryLocationCell(record.deliveryLocation)}
            </span>
          </span>
        ),
      },
      {
        title: renderColumnTitle(t('orders:table.references')),
        key: 'clientOrderNumber',
        ...getColumnSortProps('clientOrderNumber'),
        width: '14%',
        render: (_: unknown, record: any) => (
          record.clientOrderNumber || record.proformaNumber ? (
            <span className="customer-orders-table__references">
              {record.clientOrderNumber && (
                <span className="customer-orders-table__reference-main">
                  {record.clientOrderNumber}
                </span>
              )}
              {record.proformaNumber && (
                <span className="customer-orders-table__reference-secondary">
                  {t('orders:table.proformaNumber')}: {record.proformaNumber}
                </span>
              )}
            </span>
          ) : (
            <Text type="secondary">{EMPTY_VALUE}</Text>
          )
        ),
      },
    ];

    // Add actions column if any quick action is available
    if (canDeleteOrder || showQuotes || canEditOrderDetails || canChangeOrderStatus) {
      cols.unshift({
        title: t('common:actions'),
        key: 'menu',
        fixed: 'left' as const,
        width: 56,
        align: 'center' as const,
        render: (_: any, record: any) => (
          <Dropdown
            trigger={['click']}
            menu={{
              items: getRowMenuItems(record),
              onClick: async ({ key, domEvent }) => {
                domEvent.stopPropagation();

                if (key === 'edit') {
                  navigate(`/app/orders/${record.id}`);
                }

                if (key === 'status') {
                  openStatusModal(record);
                }

                if (key === 'convert') {
                  try {
                    await api.post(`/orders/${record.id}/convert-quote`);
                    feedback.success(t('orders:quoteConverted', { orderNumber: getDisplayOrderNumber(record) }));
                    fetchOrders();
                  } catch (error: any) {
                    feedback.error(getApiErrorMessage(error, t, 'orders:quoteConvertError', undefined, {
                      orderNumber: getDisplayOrderNumber(record),
                    }));
                  }
                }

                if (key === 'delete') {
                  setOrderPendingDelete(record);
                }
              }
            }}
          >
            <Button
              className="orders-row-menu-button"
              size="small"
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
              aria-label={t('orders:menu.open')}
            />
          </Dropdown>
        ),
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDeleteOrder, canCreateOrder, canEditOrderDetails, canChangeOrderStatus, filters.sortBy, filters.sortOrder, showQuotes, supplierNamesById, t]);

  const sortOptions = useMemo(
    () => [
      { value: 'date' as const, label: t('orders:table.date') },
      { value: 'orderNumber' as const, label: t('orders:table.orderNumber') },
      { value: 'producer' as const, label: t('orders:table.producer') },
      { value: 'customer' as const, label: t('orders:table.customer') },
      { value: 'contact' as const, label: t('orders:table.contact') },
      { value: 'products' as const, label: t('orders:table.products') },
      { value: 'deliveryLocation' as const, label: t('orders:table.deliveryLocation') },
      { value: 'clientOrderNumber' as const, label: t('orders:table.clientOrderNumber') },
      { value: 'proformaNumber' as const, label: t('orders:table.proformaNumber') },
      { value: 'status' as const, label: t('orders:statusFilter') },
      { value: 'accountManager' as const, label: t('orders:accountManager') },
    ],
    [t]
  );

  const supplierSortOptions = useMemo(() => {
    const options = [
      { value: 'date' as SupplierOrderSortField, label: t('orders:table.date') },
      { value: 'orderNumber' as SupplierOrderSortField, label: t('orders:supplierOrders.table.supplierOrderNumber') },
      { value: 'status' as SupplierOrderSortField, label: t('orders:statusFilter') },
      { value: 'supplier' as SupplierOrderSortField, label: t('orders:supplierOrders.table.supplier') },
      { value: 'contact' as SupplierOrderSortField, label: t('orders:supplierOrders.table.contact') },
      { value: 'factory' as SupplierOrderSortField, label: t('orders:supplierOrders.table.factory') },
      { value: 'products' as SupplierOrderSortField, label: t('orders:productsFilter') },
    ];

    if (canUseSupplierOrderFinancialSortFields(canViewSupplierOrderPurchasePrices)) {
      options.push(
        { value: 'totalAmount' as SupplierOrderSortField, label: t('orders:supplierOrders.table.totalAmount') },
        { value: 'totalAmountPLN' as SupplierOrderSortField, label: t('orders:supplierOrders.table.totalAmountPLN') }
      );
    }

    return options;
  }, [canViewSupplierOrderPurchasePrices, t]);

  const updateFilter = <K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) => {
    setFilters((current) => {
      const nextFilters = {
        ...current,
        [key]: value,
      };

      if (key === 'status' || key === 'search' || key === 'orderNumber' || key === 'deadline') {
        syncUrlBackedFilters(nextFilters);
      }

      return nextFilters;
    });
  };

  const updateCustomerTableSort = (sortBy: OrderTableSortField, sortOrder: 'asc' | 'desc') => {
    setFilters((current) => ({
      ...current,
      sortBy,
      sortOrder,
    }));
  };

  const handleCustomerTableChange: TableProps<any>['onChange'] = (_pagination, _tableFilters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter.find((item) => item.order) : sorter;
    const columnKey = activeSorter?.columnKey;

    if (!activeSorter?.order || !isCustomerTableSortField(columnKey)) {
      return;
    }

    updateCustomerTableSort(columnKey, activeSorter.order === 'ascend' ? 'asc' : 'desc');
  };

  const updateSupplierFilter = <K extends keyof SupplierOrdersPageFilters>(key: K, value: SupplierOrdersPageFilters[K]) => {
    setSupplierFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const setSupplierStatusPreset = (status: string[]) => {
    setSupplierFilters((current) => ({
      ...current,
      status,
    }));
  };

  const rowSelection = {
    selectedRowKeys: selectedOrderIds,
    onChange: (keys: React.Key[]) => setSelectedOrderIds(keys.map(String)),
    preserveSelectedRowKeys: false,
    columnWidth: 54,
  };

  const renderOrderScopeTabs = () => {
    if (!canViewSupplierOrders) {
      return null;
    }

    return (
      <Tabs
        className="app-page__tabs"
        activeKey={orderFlow}
        onChange={(key) => (key === 'supplier' ? showSupplierOrderFlow() : showCustomerOrderFlow())}
        items={[
          { key: 'customer', label: <span><TeamOutlined /> {t('orders:customerOrdersTab')}</span> },
          { key: 'supplier', label: <span><ShopOutlined /> {t('orders:supplierOrdersTab')}</span> },
        ]}
      />
    );
  };

  const customerViewValue = showQuotes
    ? 'quotes'
    : filters.status.length === 0
      ? 'all'
      : orderStatusFilterMatchesView(filters.status, NEW_ORDER_VIEW_STATUSES)
        ? 'new'
        : orderStatusFilterMatchesView(filters.status, IN_PROGRESS_ORDER_VIEW_STATUSES)
          ? 'inProgress'
          : 'custom';

  const renderCustomerViewSwitch = () => (
    <Segmented
      aria-label={t('orders:views.label')}
      value={customerViewValue}
      onChange={(value) => {
        if (value === 'all') showAllOrdersView();
        if (value === 'new') showNewOrdersView();
        if (value === 'inProgress') showInProgressOrdersView();
        if (value === 'quotes') showQuotesView();
      }}
      options={[
        { value: 'all', label: `${t('orders:views.all')} (${orders.length})` },
        { value: 'new', label: `${t('orders:views.new')} (${newOrdersCount})` },
        { value: 'inProgress', label: `${t('orders:views.inProgress')} (${inProgressOrdersCount})` },
        { value: 'quotes', label: t('orders:quotesTab') },
      ]}
    />
  );

  const supplierViewValue = supplierFilters.status.length === 0
    ? 'all'
    : supplierFilters.status.length === 1 && supplierFilters.status[0] === 'DRAFT'
      ? 'new'
      : supplierFilters.status.length === 1 && supplierFilters.status[0] === 'RECEIVED'
        ? 'received'
        : supplierFilters.status.length === SUPPLIER_ORDER_IN_PROGRESS_STATUSES.length
          && SUPPLIER_ORDER_IN_PROGRESS_STATUSES.every((status) => supplierFilters.status.includes(status))
          ? 'inProgress'
          : 'custom';

  const renderSupplierViewSwitch = () => (
    <Segmented
      aria-label={t('orders:views.label')}
      value={supplierViewValue}
      onChange={(value) => {
        if (value === 'all') setSupplierStatusPreset([]);
        if (value === 'new') setSupplierStatusPreset(['DRAFT']);
        if (value === 'inProgress') setSupplierStatusPreset(SUPPLIER_ORDER_IN_PROGRESS_STATUSES);
        if (value === 'received') setSupplierStatusPreset(['RECEIVED']);
      }}
      options={[
        { value: 'all', label: t('orders:views.all') },
        { value: 'new', label: t('orders:views.new') },
        { value: 'inProgress', label: t('orders:views.inProgress') },
        { value: 'received', label: t('orders:supplierOrders.views.received') },
      ]}
    />
  );

  const renderOrdersToolbar = () => (
    <div className="app-page__toolbar">
      {!isTester ? (
        <Input
          className="app-page__toolbar-search"
          placeholder={t('orders:search')}
          prefix={<SearchOutlined />}
          value={orderFlow === 'supplier' ? supplierFilters.search : filters.search}
          onChange={(event) => {
            if (orderFlow === 'supplier') {
              updateSupplierFilter('search', event.target.value);
              return;
            }
            updateFilter('search', event.target.value);
          }}
          allowClear
        />
      ) : <span />}
      <div className="app-page__toolbar-actions">
        {!isTester && (
          <>
            {orderFlow === 'supplier' ? renderSupplierViewSwitch() : renderCustomerViewSwitch()}
            <Badge count={currentActiveFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || currentActiveFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || currentActiveFilterCount > 0}
                onClick={() => setFiltersOpen((current) => !current)}
              >
                {t('orders:filters')}
              </Button>
            </Badge>
            {currentActiveFilterCount > 0 && (
              <Button type="link" onClick={clearCurrentFilters}>
                {t('orders:clearFilters')}
              </Button>
            )}
            <span className="app-page__count">
              {orderFlow === 'supplier' ? supplierFilteredCount : displayedOrders.length}
            </span>
          </>
        )}
        {(canCreateOrder || canCreateSupplierOrder) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateOrderChoiceModal}>
            {t('orders:newOrder')}
          </Button>
        )}
      </div>
    </div>
  );

  const renderSupplierFiltersPanel = () => (
    filtersOpen ? (
      <div className="app-page__filters">
        <Row gutter={[16, 16]}>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:search')}</Text>
              <Input
                placeholder={t('orders:searchPlaceholder')}
                prefix={<SearchOutlined />}
                value={supplierFilters.search}
                onChange={(event) => updateSupplierFilter('search', event.target.value)}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:orderNumberFilter')}</Text>
              <Input
                placeholder={t('orders:orderNumberFilter')}
                value={supplierFilters.orderNumber}
                onChange={(event) => updateSupplierFilter('orderNumber', event.target.value)}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:statusFilter')}</Text>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder={t('orders:statusFilter')}
                value={supplierFilters.status}
                onChange={(value) => updateSupplierFilter('status', value)}
                allowClear
              >
                {SUPPLIER_ORDER_STATUS_FILTER_OPTIONS.map((status) => (
                  <Select.Option key={status} value={status}>
                    {t(`orders:supplierOrders.status.${status}`)}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:supplierOrders.table.supplier')}</Text>
              <Input
                placeholder={t('orders:supplierOrders.table.supplier')}
                value={supplierFilters.supplier}
                onChange={(event) => updateSupplierFilter('supplier', event.target.value)}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:supplierOrders.table.factory')}</Text>
              <Input
                placeholder={t('orders:supplierOrders.table.factory')}
                value={supplierFilters.factory}
                onChange={(event) => updateSupplierFilter('factory', event.target.value)}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:productsFilter')}</Text>
              <Input
                placeholder={t('orders:productsFilter')}
                value={supplierFilters.products}
                onChange={(event) => updateSupplierFilter('products', event.target.value)}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:proformaNumberFilter')}</Text>
              <Input
                placeholder={t('orders:proformaNumberFilter')}
                value={supplierFilters.proformaNumber}
                onChange={(event) => updateSupplierFilter('proformaNumber', event.target.value)}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">{t('orders:dateRange')}</Text>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={supplierFilters.dateRange}
                onChange={(dates) =>
                  updateSupplierFilter('dateRange', dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)
                }
                placeholder={[t('orders:startDate'), t('orders:endDate')]}
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={12} lg={8}>
            <div className="orders-filter-field orders-filter-field--sort">
              <Text className="orders-filter-field__label">{t('orders:sortBy')}</Text>
              <div className="orders-sort-control">
                <Select
                  style={{ width: '100%' }}
                  value={supplierFilters.sortBy}
                  onChange={(value) => updateSupplierFilter('sortBy', value)}
                >
                  {supplierSortOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
                <Tooltip
                  title={supplierFilters.sortOrder === 'asc' ? t('orders:ascending') : t('orders:descending')}
                >
                  <Button
                    className="orders-sort-control__direction"
                    aria-label={supplierFilters.sortOrder === 'asc' ? t('orders:ascending') : t('orders:descending')}
                    icon={supplierFilters.sortOrder === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    onClick={() =>
                      updateSupplierFilter('sortOrder', supplierFilters.sortOrder === 'asc' ? 'desc' : 'asc')
                    }
                  />
                </Tooltip>
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} md={12} lg={4}>
            <div className="orders-filter-field">
              <Text className="orders-filter-field__label">&nbsp;</Text>
              <Button
                icon={<ClearOutlined />}
                onClick={clearSupplierFilters}
                disabled={supplierActiveFilterCount === 0}
              >
                {t('orders:clearFilters')}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    ) : null
  );

  const renderCreateChoiceModal = () => (
    <Modal
      title={t('orders:createChoice.title')}
      open={createChoiceVisible}
      onCancel={() => setCreateChoiceVisible(false)}
      footer={null}
      width={520}
    >
      <div className="orders-create-choice">
        {canCreateOrder && (
          <button type="button" className="orders-create-choice__option" onClick={handleCreateCustomerOrderChoice}>
            <TeamOutlined aria-hidden="true" />
            <span>
              <strong>{t('orders:createChoice.customer.title')}</strong>
              <small>{t('orders:createChoice.customer.description')}</small>
            </span>
          </button>
        )}
        {canCreateSupplierOrder && (
          <button type="button" className="orders-create-choice__option" onClick={handleCreateSupplierOrderChoice}>
            <ShopOutlined aria-hidden="true" />
            <span>
              <strong>{t('orders:createChoice.supplier.title')}</strong>
              <small>{t('orders:createChoice.supplier.description')}</small>
            </span>
          </button>
        )}
      </div>
    </Modal>
  );

  if (orderFlow === 'supplier') {
    return (
      <div className="app-page orders-page">
        {renderOrderScopeTabs()}
        <section className="app-page__card">
        {renderOrdersToolbar()}
        {!isTester && renderSupplierFiltersPanel()}
        <SupplierOrdersPanel
          filters={supplierTableFilters}
          onSortChange={(sortBy, sortOrder) => {
            setSupplierFilters((current) => ({
              ...current,
              sortBy,
              sortOrder,
            }));
          }}
          onFilteredCountChange={setSupplierFilteredCount}
          showActionBar={false}
        />
        </section>
        {renderCreateChoiceModal()}
      </div>
    );
  }

  return (
    <div className="app-page orders-page">
      {renderOrderScopeTabs()}
      <section className="app-page__card">
      {renderOrdersToolbar()}

      {isTester && (
        <div className="app-page__card-body">
          <Text type="secondary">{t('orders:testerMode', 'Tryb testowy — utwórz nowe zamówienie przyciskiem powyżej')}</Text>
        </div>
      )}

      {!isTester && <>

      {filtersOpen && (
        <div className="app-page__filters">
                <Row gutter={[16, 16]}>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:search')}</Text>
                      <Input
                        placeholder={t('orders:searchPlaceholder')}
                        prefix={<SearchOutlined />}
                        value={filters.search}
                        onChange={(e) => updateFilter('search', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:orderNumberFilter')}</Text>
                      <Input
                        placeholder={t('orders:orderNumberFilter')}
                        value={filters.orderNumber}
                        onChange={(e) => updateFilter('orderNumber', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:statusFilter')}</Text>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder={t('orders:statusFilter')}
                        value={filters.status}
                        onChange={(value) => updateFilter('status', value)}
                        allowClear
                      >
                        {ORDER_STATUS_FILTER_OPTIONS.map((status) => (
                          <Select.Option key={status} value={status}>
                            {t(`orders:statusOptions.${status}`)}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:customerFilter')}</Text>
                      <Select
                        style={{ width: '100%' }}
                        placeholder={t('orders:customerFilter')}
                        value={filters.customerId}
                        onChange={(value) => updateFilter('customerId', value)}
                        showSearch
                        filterOption={filterCustomerOption}
                        allowClear
                        options={customerSelectOptions}
                        optionRender={renderCustomerOption}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:accountManager')}</Text>
                      <Select
                        style={{ width: '100%' }}
                        placeholder={t('orders:accountManager')}
                        value={filters.accountManagerId}
                        onChange={(value) => updateFilter('accountManagerId', value)}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {users.map((user: any) => (
                          <Select.Option key={user.id} value={user.id}>
                            {user.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:factoryFilter')}</Text>
                      <Input
                        placeholder={t('orders:factoryFilter')}
                        value={filters.factory}
                        onChange={(e) => updateFilter('factory', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:productsFilter')}</Text>
                      <Input
                        placeholder={t('orders:productsFilter')}
                        value={filters.products}
                        onChange={(e) => updateFilter('products', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:dateRange')}</Text>
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        value={filters.dateRange}
                        onChange={(dates) => updateFilter('dateRange', dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
                        placeholder={[t('orders:startDate'), t('orders:endDate')]}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:deliveryLocationFilter')}</Text>
                      <Input
                        placeholder={t('orders:deliveryLocationFilter')}
                        value={filters.deliveryLocation}
                        onChange={(e) => updateFilter('deliveryLocation', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:clientOrderNumberFilter')}</Text>
                      <Input
                        placeholder={t('orders:clientOrderNumberFilter')}
                        value={filters.clientOrderNumber}
                        onChange={(e) => updateFilter('clientOrderNumber', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">{t('orders:proformaNumberFilter')}</Text>
                      <Input
                        placeholder={t('orders:proformaNumberFilter')}
                        value={filters.proformaNumber}
                        onChange={(e) => updateFilter('proformaNumber', e.target.value)}
                        allowClear
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={12} lg={8}>
                    <div className="orders-filter-field orders-filter-field--sort">
                      <Text className="orders-filter-field__label">{t('orders:sortBy')}</Text>
                      <div className="orders-sort-control">
                        <Select
                          style={{ width: '100%' }}
                          value={filters.sortBy}
                          onChange={(value) => updateFilter('sortBy', value)}
                        >
                          {sortOptions.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                        <Tooltip
                          title={filters.sortOrder === 'asc' ? t('orders:ascending') : t('orders:descending')}
                        >
                          <Button
                            className="orders-sort-control__direction"
                            aria-label={filters.sortOrder === 'asc' ? t('orders:ascending') : t('orders:descending')}
                            icon={filters.sortOrder === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                            onClick={() =>
                              updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')
                            }
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={12} lg={4}>
                    <div className="orders-filter-field">
                      <Text className="orders-filter-field__label">&nbsp;</Text>
                      <Button
                        icon={<ClearOutlined />}
                        onClick={clearFilters}
                        disabled={activeFilterCount === 0}
                      >
                        {t('orders:clearFilters')}
                      </Button>
                    </div>
                  </Col>
                </Row>
        </div>
      )}

      {selectedOrderIds.length > 0 && (
      <div className="app-page__bulkbar">
        <div className="app-page__bulkbar-copy">
          <span className="app-page__bulkbar-label">{t('orders:bulk.label')}</span>
          <span className="app-page__bulkbar-count">
            {selectedOrderIds.length > 0
              ? t('orders:bulk.selectedCount', { count: selectedOrderIds.length })
              : t('orders:bulk.empty')}
          </span>
        </div>

        <div className="app-page__bulkbar-actions">
          {showQuotes && canManageOrderOperations && (
            <Button
              onClick={handleBulkConvert}
              disabled={selectedOrderIds.length === 0}
              loading={bulkActionLoading === 'convert'}
            >
              {t('orders:bulk.convert')}
            </Button>
          )}
          {canDeleteOrder && (
            <Button
              danger
              onClick={handleBulkDelete}
              disabled={selectedOrderIds.length === 0}
              loading={bulkActionLoading === 'delete'}
            >
              {t('orders:bulk.delete')}
            </Button>
          )}
          <Button onClick={clearSelection} disabled={selectedOrderIds.length === 0}>
            {t('orders:bulk.clearSelection')}
          </Button>
        </div>
      </div>
      )}

        <Table
          className="orders-table orders-table--customer orders-table--summary"
          columns={columns}
          dataSource={displayedOrders}
          onRow={(record) => ({
            tabIndex: 0,
            style: { cursor: 'pointer' },
            onClick: (event) => {
              if ((event.target as HTMLElement).closest('a,button,input,label,[role="button"],[role="checkbox"],.ant-dropdown-trigger,.ant-select')) return;
              if (window.getSelection()?.toString()) return;
              navigate(`/app/orders/${record.id}`);
            },
            onKeyDown: (event) => {
              if (event.target === event.currentTarget && event.key === 'Enter') navigate(`/app/orders/${record.id}`);
            },
          })}
          rowKey="id"
          rowSelection={rowSelection}
          loading={loading}
          onChange={handleCustomerTableChange}
          sortDirections={['ascend', 'descend', 'ascend']}
          tableLayout="fixed"
          pagination={{
            showSizeChanger: false
          }}
          locale={{
            emptyText: t('orders:noOrders')
          }}
        />
      </>}
      </section>

      <Modal
        title={t('orders:confirmDeleteOrder')}
        open={Boolean(orderPendingDelete)}
        onCancel={() => setOrderPendingDelete(null)}
        onOk={async () => {
          if (!orderPendingDelete) return;
          await handleDeleteOrder(
            orderPendingDelete.id,
            getDisplayOrderNumber(orderPendingDelete),
            orderPendingDelete.customer?.name
          );
          setOrderPendingDelete(null);
        }}
        okText={t('common:yes')}
        cancelText={t('common:no')}
        okButtonProps={{ danger: true }}
      >
        <p>{t('orders:confirmDeleteOrder')}</p>
      </Modal>

      <Modal
        title={t('orders:orderDetail.updateStatus')}
        open={Boolean(orderPendingStatusChange)}
        onCancel={() => {
          setOrderPendingStatusChange(null);
          statusForm.resetFields();
        }}
        footer={null}
        maskClosable={false}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusChange}
          onFinishFailed={() => feedback.warning(t('orders:messages.fillRequired'))}
          scrollToFirstError
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('orders:statusSelection.informationalTitle')}
            description={t('orders:statusSelection.informationalDescription')}
          />
          <Form.Item
            name="status"
            label={t('orders:orderDetail.newStatus')}
            rules={[{ required: true }]}
          >
            <OrderStatusTransitionSelect
              statuses={CUSTOMER_ORDER_ACTIVE_STATUS_VALUES}
              currentStatus={orderPendingStatusChange?.status || ''}
              allowedTransitions={getOrderAllowedTransitions(orderPendingStatusChange)}
              getStatusLabel={(status) => t(`orders:statusOptions.${status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
              guardedStatuses={['ON_HOLD']}
              guardedStatusReason={t('orders:statusSelection.requiresExplicitAction')}
              guardedStatusActionLabel={() => t('orders:statusSelection.holdOrderAction')}
              guardedStatusHint={t('orders:statusSelection.holdOrderHint')}
              onGuardedStatusSelect={(status) => statusForm.setFieldValue('status', status)}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={statusUpdating}>
                {t('orders:orderDetail.updateStatus')}
              </Button>
              <Button
                onClick={() => {
                  setOrderPendingStatusChange(null);
                  statusForm.resetFields();
                }}
                disabled={statusUpdating}
              >
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {renderCreateChoiceModal()}

    </div>
  );
};

export default Orders;
